import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

@Component({
  selector: 'app-staff-nav',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './staff-nav.component.html',
  styleUrl: './staff-nav.component.scss'
})
export class StaffNavComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isMobileMenuOpen = false;
  @Output() navigate = new EventEmitter<string>();
  @Output() mobileMenuClose = new EventEmitter<void>();
  @Output() desktopSidebarToggle = new EventEmitter<boolean>();

  isDesktopSidebarCollapsed = false;
  private resizeHandler: () => void;

  constructor(private router: Router) {
    this.resizeHandler = this.handleResize.bind(this);
  }

  ngOnInit(): void {
    // Add window resize listener
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  ngOnDestroy(): void {
    // Clean up
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeHandler);
    }
    this.enableBodyScroll();
  }

  ngOnChanges(): void {
    // Handle body scroll when menu state changes
    if (this.isMobileMenuOpen) {
      this.disableBodyScroll();
    } else {
      this.enableBodyScroll();
    }
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();
    
    switch (route) {
      case 'dashboard':
        this.router.navigate(['/staff/staff-dashboard']);
        break;
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'reference-user-data':
        this.router.navigate(['/staff/reference-user-data-management']);
        break;
      case 'roles':
        this.router.navigate(['/staff/role-management']);
        break;
      case 'organizations':
        this.router.navigate(['/staff/organization-management']);
        break;
      case 'staff':
        this.router.navigate(['/staff/staff-management']);
        break;
      case 'courses':
        this.router.navigate(['/staff/course-management']);
        break;
      case 'exams':
        this.router.navigate(['/staff/exam-management']);
        break;
      case 'request-regis-new-user':
        this.router.navigate(['/staff/request-regis-new-user-management']);
        break;
      case 'request-renew-member':
        this.router.navigate(['/staff/request-renew-member-management']);
        break;
      case 'request-regis-appraiser':
        this.router.navigate(['/staff/request-regis-new-appraiser']);
        break;
      case 'request-renew-appraiser':
        this.router.navigate(['/staff/request-renew-appraiser']);
        break;
      case 'cpd-hours':
        this.router.navigate(['/staff/cpd-hours-management']);
        break;
      case 'exam-lists':
        this.router.navigate(['/staff/exam-list-management']);
        break;
      case 'payment-verification':
        this.router.navigate(['/staff/payment-verification']);
        break;
      case 'account-worklist':
        this.router.navigate(['/staff/account-worklist']);
        break;
      case 'reports':
        this.navigate.emit('reports');
        break;
      case 'settings':
        this.navigate.emit('settings');
        break;
      default:
        this.navigate.emit(route);
        break;
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (this.isMobileMenuOpen) {
      this.disableBodyScroll();
    } else {
      this.enableBodyScroll();
    }
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.enableBodyScroll();
    this.mobileMenuClose.emit();
  }

  private disableBodyScroll(): void {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      if (window.innerWidth <= 1024) {
        document.body.classList.add('menu-open');
        document.body.style.overflow = 'hidden';
      }
    }
  }

  private enableBodyScroll(): void {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    }
  }

  private handleResize(): void {
    if (typeof window !== 'undefined') {
      // Close mobile menu when switching to desktop
      if (window.innerWidth > 1024 && this.isMobileMenuOpen) {
        this.closeMobileMenu();
      }
      
      // Enable body scroll on desktop
      if (window.innerWidth > 1024) {
        this.enableBodyScroll();
      }
    }
  }

  toggleDesktopSidebar(): void {
    this.isDesktopSidebarCollapsed = !this.isDesktopSidebarCollapsed;
    this.desktopSidebarToggle.emit(this.isDesktopSidebarCollapsed);
  }
} 